import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Layout } from "@/components/layout";
import { formatDate } from "@/lib/date-utils";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Pencil, Plus, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { formatCentsAsDollars } from "@/lib/price-utils";
import { useCustomParts } from "@/hooks/use-custom-parts";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import type { CustomPart } from "@onsemble/shared";

export default function CustomPartsPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingCustomPart, setEditingCustomPart] = useState<CustomPart | null>(
    null,
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [customPartToDelete, setCustomPartToDelete] =
    useState<CustomPart | null>(null);

  // Form states
  const [title, setTitle] = useState("");
  const [cost, setCost] = useState("");

  // Get custom parts from backend
  const {
    customParts,
    isLoading,
    createCustomPart,
    updateCustomPart,
    deleteCustomPart,
  } = useCustomParts();

  // Handle adding a custom part
  const handleAddCustomPart = async () => {
    if (!title.trim() || !cost) {
      toast({
        title: t("common.error"),
        description: t("pages.admin.customParts.pleaseFillInAll"),
        variant: "destructive",
      });
      return;
    }

    try {
      await createCustomPart({
        title: title.trim(),
        cost: Math.round(parseFloat(cost) * 100), // Convert to cents
      });

      toast({
        title: t("pages.admin.customParts.success"),
        description: t("pages.admin.customParts.customPartAddedSuccessfully"),
      });

      setIsAddDialogOpen(false);
      setTitle("");
      setCost("");
    } catch (error) {
      toast({
        title: t("common.error"),
        description: t("pages.admin.customParts.failedToAddCustom"),
        variant: "destructive",
      });
    }
  };

  // Handle editing a custom part
  const handleEditCustomPart = async () => {
    if (!editingCustomPart || !title.trim() || !cost) {
      toast({
        title: t("common.error"),
        description: t("pages.admin.customParts.pleaseFillInAll"),
        variant: "destructive",
      });
      return;
    }

    try {
      await updateCustomPart(editingCustomPart.uid, {
        title: title.trim(),
        cost: Math.round(parseFloat(cost) * 100), // Convert to cents
      });

      toast({
        title: t("pages.admin.customParts.success"),
        description: t("pages.admin.customParts.customPartUpdatedSuccessfully"),
      });

      setIsEditDialogOpen(false);
      setEditingCustomPart(null);
      setTitle("");
      setCost("");
    } catch (error) {
      toast({
        title: t("common.error"),
        description: t("pages.admin.customParts.failedToUpdateCustom"),
        variant: "destructive",
      });
    }
  };

  // Handle deleting a custom part
  const handleDeleteCustomPart = async () => {
    if (!customPartToDelete) return;

    try {
      await deleteCustomPart(customPartToDelete.uid);

      toast({
        title: t("pages.admin.customParts.success"),
        description: t("pages.admin.customParts.customPartDeletedSuccessfully"),
      });

      setIsDeleteDialogOpen(false);
      setCustomPartToDelete(null);
    } catch (error) {
      toast({
        title: t("common.error"),
        description: t("pages.admin.customParts.failedToDeleteCustom"),
        variant: "destructive",
      });
    }
  };

  // Open edit dialog
  const openEditDialog = (customPart: CustomPart) => {
    setEditingCustomPart(customPart);
    setTitle(customPart.title);
    setCost((customPart.cost / 100).toString()); // Convert from cents
    setIsEditDialogOpen(true);
  };

  // Open delete confirmation dialog
  const openDeleteDialog = (customPart: CustomPart) => {
    setCustomPartToDelete(customPart);
    setIsDeleteDialogOpen(true);
  };

  // Open add dialog
  const openAddDialog = () => {
    setTitle("");
    setCost("");
    setIsAddDialogOpen(true);
  };

  return (
    <Layout headerContent={
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">{t("common.admin")}</span>
        <span className="text-muted-foreground">/</span>
        <span className="font-semibold">{t("pages.admin.customParts.customParts")}</span>
      </div>
    }>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold mb-2">{t("pages.admin.customParts.customParts")}</h1>
            <p className="text-muted-foreground">
              {t("pages.admin.customParts.manageYourCustomParts")}
            </p>
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" onClick={() => navigate("/admin")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t("pages.admin.customParts.backToAdmin")}
            </Button>
            <Button onClick={openAddDialog}>
              <Plus className="h-4 w-4 mr-2" />
              {t("pages.admin.customParts.addCustomPart")}
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center p-8">
            <p>{t("pages.admin.customParts.loadingCustomParts")}</p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {customParts && customParts.length > 0 ? (
              customParts.map((customPart: CustomPart) => (
                <Card key={customPart.uid} className="flex flex-col">
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-xl font-semibold">
                        {customPart.title}
                      </CardTitle>
                      <Badge
                        variant="default"
                        className="bg-green-600 text-white"
                      >
                        ${formatCentsAsDollars(customPart.cost)}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="flex-grow">
                    <p className="text-sm text-muted-foreground">
                      {t("pages.admin.customParts.addedOn")} {formatDate(customPart.createdAt)}
                    </p>
                  </CardContent>
                  <CardFooter className="flex justify-end space-x-2 pt-3 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditDialog(customPart)}
                    >
                      <Pencil className="h-4 w-4 mr-2" />
                      {t("pages.admin.customParts.edit")}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openDeleteDialog(customPart)}
                      className="h-8 w-8"
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                    </Button>
                  </CardFooter>
                </Card>
              ))
            ) : (
              <div className="col-span-full p-8 text-center bg-muted rounded-lg">
                <p className="text-muted-foreground">
                  {t("pages.admin.customParts.noCustomPartsFound")}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Custom Part Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t("pages.admin.customParts.addCustomPart")}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="title">{t("pages.admin.customParts.title")}</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t("pages.admin.customParts.enterCustomPartTitle")}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="cost">{t("pages.admin.customParts.cost")}</Label>
              <Input
                id="cost"
                type="number"
                step="0.01"
                min="0"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleAddCustomPart}>{t("pages.admin.customParts.addCustomPart")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Custom Part Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t("pages.admin.customParts.editCustomPart")}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-title">{t("pages.admin.customParts.title")}</Label>
              <Input
                id="edit-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t("pages.admin.customParts.enterCustomPartTitle")}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-cost">{t("pages.admin.customParts.cost")}</Label>
              <Input
                id="edit-cost"
                type="number"
                step="0.01"
                min="0"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsEditDialogOpen(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button onClick={handleEditCustomPart}>{t("pages.admin.customParts.updateCustomPart")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t("pages.admin.customParts.deleteCustomPart")}</DialogTitle>
            <DialogDescription>
              {t("pages.admin.customParts.areYouSureYou")}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {customPartToDelete && (
              <div className="p-4 border rounded-md">
                <p className="font-semibold">{customPartToDelete.title}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {t("pages.admin.customParts.cost_2")}{formatCentsAsDollars(customPartToDelete.cost)}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button variant="destructive" onClick={handleDeleteCustomPart}>
              {t("pages.admin.customParts.deleteCustomPart")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
